import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Calendar,
  User,
  IndianRupee,
  Package,
  Loader2,
  AlertCircle,
} from "lucide-react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import { showError } from "../../../utils/toastUtils";
import AssignProductsModal from "./AssignProductsModal";

const AssignProducts = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [existingAssignments, setExistingAssignments] = useState(null);


  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
      fetchExistingAssignments();
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/service-bookings/get-by-id/${bookingId}`
      );

      setBooking(response.data.data);
    } catch (error) {
      console.error("Failed to fetch booking:", error);
      showError("Failed to load booking details");
      navigate("/bookings");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExistingAssignments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products-assignment/${bookingId}`
      );

      setExistingAssignments(response.data.data);
    } catch (error) {
      // It's okay if no assignments exist yet
      console.log("No existing assignments");
    }
  };
  // console.log(existingAssignments);

  const handleAssignProducts = (service) => {
    if (!booking?.staffId?._id) {
      showError("Please assign a staff member to this booking first");
      return;
    }

    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleSuccess = () => {
    fetchExistingAssignments();
  };

  const getAssignedProductsForService = (serviceCategoryName) => {
    if (!existingAssignments) return [];

    // Combine both regular assigned products and approved requested products
    const regularProducts = existingAssignments.products?.filter(
      (p) => p.product?.service?.toLowerCase() === serviceCategoryName.toLowerCase()
    ) || [];

    // Get approved requested products that match this service category
    const requestedProducts = existingAssignments.requestedProducts?.filter(
      (rp) => rp.status === "approved" &&
        rp.product?.service?.toLowerCase() === serviceCategoryName.toLowerCase()
    ) || [];

    // Combine both arrays
    return [...regularProducts, ...requestedProducts];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="animate-spin text-gray-400" size={48} />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-500">
        <AlertCircle size={48} className="mb-4" />
        <p>Booking not found</p>
      </div>
    );
  }

  return (
    <>
      <div className="px-6 py-3">
        <Breadcrumb title1="Bookings" title2="Assign Products" />
      </div>

      <div className="mx-6 mb-6 bg-white rounded-md shadow-lg p-6">
        {/* Back Button */}
        <button
          onClick={() => navigate("/bookings")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <ArrowLeft size={20} />
          Back to Bookings
        </button>

        {/* Booking Summary */}
        <div className="bg-gradient-to-r  from-accentTextColor/80 via-[#e8ab99] to-accentTextColor/80 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Booking Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg">
                <Calendar size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Booking ID</p>
                <p className="font-semibold text-gray-800">
                  {booking._id?.slice(-8).toUpperCase()}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg">
                <User size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Customer</p>
                <p className="font-semibold text-gray-800">
                  {booking.userId?.name || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg">
                <User size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Assigned Staff</p>
                <p className="font-semibold text-gray-800">
                  {booking.staffId?.name || "Not assigned"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg">
                <IndianRupee size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Total Amount</p>
                <p className="font-semibold text-gray-800">
                  ₹{booking.totalAmount || 0}
                </p>
              </div>
            </div>
          </div>

          {!booking?.staffId?._id && (
            <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-600 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-800">
                  Staff Not Assigned
                </p>
                <p className="text-sm text-yellow-700">
                  Please assign a staff member to this booking before assigning
                  products.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Services List */}
        <div>
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Package size={20} />
            Services & Product Assignment
          </h3>

          {booking.services?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No services in this booking</p>
            </div>
          ) : (
            <div className="space-y-4">
              {booking.services?.map((service, index) => {
                const categoryName =
                  service?.serviceId?.categoryId?.categoryName || "Other";
                const assignedProducts =
                  getAssignedProductsForService(categoryName);
                // console.log(service)
                return (
                  <div
                    key={service._id || index}
                    className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Service Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          {service?.serviceId?.imageUrl ? (
                            <img
                              src={service.serviceId.imageUrl}
                              alt={service.serviceId.name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                              <Package size={24} className="text-gray-400" />
                            </div>
                          )}

                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800 text-lg">
                              {service.serviceId?.name || "Service"}
                            </h4>
                            {service.brand && (
                              <p className="text-sm text-gray-600">
                                Brand:{" "}
                                <span className="font-medium">
                                  {service.brand}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Service Details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500">Category</p>
                            <p className="font-medium text-gray-800">
                              {categoryName || "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Sub-Category</p>
                            <p className="font-medium text-gray-800">
                              {service?.serviceId?.subCategoryId
                                ?.subCategoryName || "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Duration</p>
                            <p className="font-medium text-gray-800">
                              {service.serviceId?.duration || "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Price</p>
                            <p className="font-medium text-gray-800">
                              ₹{service.price || 0}
                            </p>
                          </div>
                        </div>

                        {/* Assigned Products */}
                        {assignedProducts.length > 0 && (
                          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-sm font-semibold text-green-800 mb-2">
                              Assigned Products ({assignedProducts.length})
                            </p>
                            <div className="space-y-1">
                              {assignedProducts.map((ap, idx) => {
                                // Check if this is a requested product (has requestedQuantity)
                                const isRequested = !!ap.requestedQuantity;
                                const quantity = isRequested
                                  ? ap.requestedQuantity
                                  : ap.assignedQuantity;
                                const productName =
                                  ap.product?.productName || "Product";

                                return (
                                  <div
                                    key={idx}
                                    className="text-xs text-green-700 flex justify-between items-center"
                                  >
                                    <span className="flex items-center gap-1">
                                      • {productName}
                                      {isRequested && (
                                        <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                                          Requested
                                        </span>
                                      )}
                                    </span>
                                    <span className="font-medium">
                                      Qty: {quantity}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Assign Button */}
                      <button
                        onClick={() => handleAssignProducts(service)}
                        disabled={!booking?.staffId?._id}
                        className="px-4 py-2 bg-primaryBtnColor text-white rounded-lg hover:bg-primaryBtnHoverColor transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                      >
                        <Package size={18} />
                        {assignedProducts.length > 0
                          ? "Update Products"
                          : "Assign Products"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Assign Products Modal */}
      <AssignProductsModal
        isOpen={isModalOpen}
        booking={booking}
        selectedService={selectedService}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default AssignProducts;